/**
 * One student's sticking patterns, with the attempts behind each.
 *
 * Used by BOTH admin surfaces: the User Detail → Patterns tab, and the
 * per-user drill-down inside the explorer. Expanding a pattern reveals its
 * encounters, each linking to that submission's analysis detail — the whole
 * point being to get from "this student keeps slipping" to the actual evidence
 * in two clicks.
 *
 * See docs/FeatureDesigns/diagnosis/sticking-patterns-surfaces.md.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { UserPatternsResponse } from './types';
import { formatWhen, trendLabel } from './format';
import './StickingPatterns.css';

export function UserStickingPatternsPanel({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['sticking-patterns-by-user', userId],
    queryFn: () => fetchWithAdmin<UserPatternsResponse>(`/console/sticking-patterns-by-user/${encodeURIComponent(userId)}`),
    enabled: !!userId,
  });

  if (q.isLoading) return <p className="lp-muted">Loading patterns…</p>;
  if (q.isError) return <p className="lp-error">Failed: {(q.error as Error).message}</p>;

  const patterns = q.data?.patterns ?? [];
  if (patterns.length === 0) {
    return (
      <p className="lp-empty">
        No sticking patterns yet. Patterns accrue from the diagnosis on each analysed
        attempt — a student with no diagnosed mistakes has none.
      </p>
    );
  }

  return (
    <div className="lp-panel">
      <table className="dt dt--compact lp-table">
        <thead>
          <tr>
            <th>Pattern</th>
            <th>Type</th>
            <th>Subject</th>
            <th>Times</th>
            <th>Trend</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {patterns.map(p => {
            const key = `${p.patternType}:${p.patternKey}`;
            const isOpen = open === key;
            return [
              <tr
                key={key}
                className={`lp-row ${isOpen ? 'lp-row--open' : ''}`}
                onClick={() => setOpen(isOpen ? null : key)}
              >
                <td>
                  <div className="lp-namecol">
                    <span className="lp-name">{p.label}</span>
                    {p.lastCause && <span className="lp-cause">{p.lastCause}</span>}
                  </div>
                </td>
                <td><span className={`lp-chip ${p.patternType === 'category' ? 'is-habit' : 'is-gap'}`}>
                  {p.patternType}
                </span></td>
                <td className="lp-dim">{p.subjects.length ? p.subjects.join(', ') : '—'}</td>
                <td>{p.recurrenceCount}</td>
                <td className="lp-dim">{trendLabel(p.trend)}</td>
                <td className="lp-dim">{p.lastSeenAt ? formatWhen(p.lastSeenAt) : '—'}</td>
              </tr>,
              isOpen && (
                <tr key={`${key}-detail`} className="lp-detail-row">
                  <td colSpan={6}>
                    <div className="lp-encounters">
                      {/* recurrenceCount can exceed the rows: the stored log is bounded. */}
                      {p.recurrenceCount > p.encounters.length && (
                        <p className="lp-note">
                          Showing the most recent {p.encounters.length} of {p.recurrenceCount} —
                          the encounter log is capped per pattern.
                        </p>
                      )}
                      {stageSummary(p.encounters) && <p className="lp-note">{stageSummary(p.encounters)}</p>}
                      <table className="dt dt--compact">
                        <thead>
                          <tr><th>When</th><th>Kind</th><th>Stage</th><th>Score</th><th>Diagnosed cause</th><th /></tr>
                        </thead>
                        <tbody>
                          {p.encounters.map(e => (
                            <tr key={e.submissionId}>
                              <td className="lp-dim">{formatWhen(e.at)}</td>
                              <td><span className="lp-kind">{e.kind.replace(/_/g, ' ')}</span></td>
                              <td className="lp-dim">{e.stage ? e.stage.replace(/_/g, ' ') : '—'}</td>
                              <td>{typeof e.correctnessScore === 'number' ? `${e.correctnessScore}/10` : '—'}</td>
                              <td>{e.cause ?? '—'}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn-link"
                                  onClick={() => navigate(`/submissions/${e.submissionId}`)}
                                >
                                  View analysis →
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {p.encounters.length === 0 && <p className="lp-muted">No encounters recorded.</p>}
                    </div>
                  </td>
                </tr>
              ),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

/** "2 at setup · 1 at answer" — where a pattern's attempts went wrong. A kind +
 *  topic pattern does not key on stage; this is its drill-down. */
function stageSummary(encounters: ReadonlyArray<{ stage?: string }>): string | undefined {
  const counts = new Map<string, number>();
  for (const e of encounters) if (e.stage) counts.set(e.stage, (counts.get(e.stage) ?? 0) + 1);
  if (counts.size === 0) return undefined;
  return [...counts].sort((a, b) => b[1] - a[1]).map(([s, n]) => `${n} at ${s.replace(/_/g, ' ')}`).join(' · ');
}
